import React, { useState, useEffect, useRef } from "react";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import {
  category,
  createCategory,
  deleteCategory,
  uploadAdminImage,
  updateCategoryImage,
  BASE_URL,
} from "./../services/api"; // Import the API functions
import "./Category.css";
import TopBar from "./../components/TopBar";

import Swal from "sweetalert2"; // Import SweetAlert
import { ShimmerDiv, ShimmerTable } from "shimmer-effects-react";

const Category = () => {
  const [categoryData, setCategoryData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false); // To handle loading state
  const fileInputRefs = useRef({}); // Store refs for each category

  const navigate = useNavigate();

  // Fetch categories from the API on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await category();
        console.log(data); // Fetch categories
        const formattedData = data.map((cat, index) => ({
          id: cat._id,
          serialNo: index + 1,
          name: cat.name,
          imageUrl: cat.imageUrl,
          date: new Date(cat.createdAt).toLocaleDateString(),
          subCategoryCount: cat.subcategories.length,
          subcategories: cat.subcategories,
        }));
        setCategoryData(formattedData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const handleCreate = async () => {
    if (!newCategory) {
      alert("Category name is required");
      return;
    }

    try {
      setLoading(true); // Set loading state
      await createCategory(newCategory);

      // Fetch updated categories after successful creation
      const updatedCategories = await category();
      const formattedData = updatedCategories.map((cat, index) => ({
        id: cat._id,
        serialNo: index + 1,
        name: cat.name,
        imageUrl: cat.imageUrl,
        date: new Date(cat.createdAt).toLocaleDateString(),
        subCategoryCount: cat.subcategories.length,
        subcategories: cat.subcategories,
      }));
      setCategoryData(formattedData);

      // Reset states
      setIsModalVisible(false);
      setNewCategory("");
      setLoading(false); // Reset loading state
    } catch (error) {
      console.error("Error creating category:", error);
      alert("Failed to create category. Please try again.");
      setLoading(false); // Reset loading state even if there's an error
    }
  };

  const handleMoreClick = (category) => {
    navigate(`/SubCategoryMore/${category.id}`, {
      state: { subcategories: category.subcategories },
    });
  };

  const handleDelete = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "Press OK to delete this category.",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        icon: "question",
        confirmButtonText: "OK",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteCategory(id);
          const updatedCategories = await category();
          const formattedData = updatedCategories.map((cat, index) => ({
            id: cat._id,
            serialNo: index + 1,
            name: cat.name,
            imageUrl: cat.imageUrl,
            date: new Date(cat.createdAt).toLocaleDateString(),
            subCategoryCount: cat.subcategories.length,
            subcategories: cat.subcategories,
          }));
          setCategoryData(formattedData);
        }
      });
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      try {
        const response = await uploadAdminImage(formData);
        return response.imageUrl;
      } catch (err) {
        console.error("Error uploading image:", err);
        return null;
      }
    }
    return null;
  };

  const handleUpdateCategoryImage = async (id, event) => {
    try {
      const imageUrl = await uploadImage(event);
      if (imageUrl) {
        await updateCategoryImage(imageUrl, id);
        const updatedCategories = await category();
        const formattedData = updatedCategories.map((cat, index) => ({
          id: cat._id,
          serialNo: index + 1,
          name: cat.name,
          imageUrl: cat.imageUrl,
          date: new Date(cat.createdAt).toLocaleDateString(),
          subCategoryCount: cat.subcategories.length,
          subcategories: cat.subcategories,
        }));
        setCategoryData(formattedData);
      }
    } catch (error) {
      console.error("Error updating category image:", error);
    }
  };
  // Show loading animation or error message if needed
  if (loading) {
    return (
      <>
        {/* <div className="p-5 grid gap-8 grid-cols-4 items-center justify-between">
          <ShimmerDiv mode="dark" height={150} width={300} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} rounded={1} />
        </div> */}
        <ShimmerTable mode="dark" row={7} col={6} border={0} borderColor={"#cbd5e1"} rounded={0.25} rowGap={16} colPadding={[10, 5, 10, 5]} />
      </>
    );
  }

  return (
    <>      <TopBar searchPlaceholder="Search Categories Here" />
      <div className="p-6">
        <div className="category-container">
          <div className="flex items-center justify-between">
            <h1>Categories</h1>
            <Button type="primary" className="create-button" onClick={toggleModal}>
              Create New Category
            </Button>
          </div>

          {isModalVisible && (
            <div className="modal-overlay">
              <div className="modal-content">
                <button className="modal-close-button" onClick={toggleModal}>
                  ×
                </button>
                <h2>Create New Category</h2>
                <input
                  type="text"
                  placeholder="Category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                />
                <Button
                  type="primary"
                  className="create-button-gradient text-white"
                  onClick={handleCreate}
                  loading={loading} // Show loading spinner on the button
                >
                  Create
                </Button>
              </div>
            </div>
          )}
          <div className="category-table-container">
            <table className="category-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Category</th>
                  <th>Sub-Category Count</th>
                  <th>Created Date</th>
                  <th>More</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {categoryData.map((category) => (
                  <tr key={category.id}>
                    <td className="flex items-center">
                      <div
                        className="circle-image"
                        style={{
                          backgroundImage: `url(${category.imageUrl})`,
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          backgroundSize: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          Swal.fire({
                            title: 'Are you sure?',
                            text: 'Do you really want to change the category image?',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Yes, change it!',
                            cancelButtonText: 'No, keep it',
                          }).then((result) => {
                            if (result.isConfirmed) {
                              if (!fileInputRefs.current[category.id]) {
                                fileInputRefs.current[category.id] = React.createRef();
                              }
                              fileInputRefs.current[category.id].current.click();

                              // If the user confirms, execute the image change logic
                              // handleUpdateCategoryImage(category.id, event);
                            } else {
                              Swal.fire(
                                'Cancelled',
                                'The category image was not changed.',
                                'info'
                              );
                            }
                          })

                        }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        ref={
                          !fileInputRefs.current[category.id]
                            ? (fileInputRefs.current[category.id] =
                              React.createRef())
                            : fileInputRefs.current[category.id]
                        }
                        style={{ display: "none" }}
                        onChange={(event) =>

                          handleUpdateCategoryImage(category.id, event)
                        }
                      />
                    </td>
                    <td>{category.name}</td>
                    <td>{category.subCategoryCount}</td>
                    <td>{category.date}</td>
                    <td>
                      <button
                        className="more-button"
                        onClick={() => handleMoreClick(category)}
                      >
                        More
                      </button>
                    </td>
                    <td>
                      <button
                        className="more-button"
                        onClick={() => handleDelete(category.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div></div></>
  );
};

export default Category;
